import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, output } from '@angular/core';
import type { FormControl } from '@angular/forms';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';

import { QrCodeModule } from 'ng-qrcode';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { noWhitespaceValidator } from '@core/common-legacy';
import { injectAuthTranslations, TwoFaService } from '@core/components-authorization-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
// eslint-disable-next-line boundaries/element-types -- FIXME
import type { TwoFactorAuthenticationSetup } from '@core/security-data';
import { SecurityAnalytics } from '@core/security-logic';
import {
  NotificationsController,
  DialogBodyComponent,
  HeadingComponent,
  DialogHeaderComponent,
  ButtonLoadingDirective,
  IconComponent,
  ButtonComponent,
  DialogFooterComponent,
  CodeInputComponent,
  LabelComponent,
  BoxComponent,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-app-activation-step-2',
  templateUrl: './app-activation-step-2.component.html',
  styleUrls: ['./app-activation-step-2.component.less'],
  imports: [
    DialogHeaderComponent,
    HeadingComponent,
    DialogBodyComponent,
    BoxComponent,
    QrCodeModule,
    InfoboxComponent,
    LabelComponent,
    CodeInputComponent,
    ReactiveFormsModule,
    DialogFooterComponent,
    ButtonComponent,
    IconComponent,
    ButtonLoadingDirective,
    TranslatePipe,
  ],
})
export class AppActivationStep2Component implements OnInit, OnDestroy {
  public translationKey = injectAuthTranslations();

  public codeControl: FormControl<string>;
  public codeError: { error: string[] } | null;
  public isLoading = false;
  public otpauthUri: string;
  public otpSecret: string;

  public goBack = output<void>();
  public activated = output<void>();
  public cancel = output<void>();

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private notificationCtrl: NotificationsController,
    private i18n: I18nService,
    private analytics: SecurityAnalytics,
    private twoFaSmsService: TwoFaService<{ error?: string[]; errors?: string[] }>,
  ) {
    this.codeControl = <FormControl<string>>(
      this.formBuilder.control('', [Validators.required, noWhitespaceValidator])
    );
  }

  public ngOnInit(): void {
    this.codeControl.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.cdRef.detectChanges());

    this.twoFaSmsService.selectTwoFaData().subscribe();

    this.twoFaSmsService
      .selectTwoFaData()
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe((setup: TwoFactorAuthenticationSetup) => {
        this.otpauthUri = setup.otpauthUri;
        this.otpSecret = setup.otpSecret;
        this.cdRef.detectChanges();
      });

    this.twoFaSmsService
      .getEnableAppAuthSuccessAction()
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        this.notificationCtrl.success({
          title: this.i18n.translate(this.translationKey.dialog_2fa_sms.success_toast.title),
        });
        this.activated.emit();
        this.isLoading = false;
        this.cdRef.detectChanges();
      });

    this.twoFaSmsService
      .getEnableAppAuthFailAction()

      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(data => {
        const error = data.payload.error;
        this.codeError = { error: error.error ? error.error : error.errors! };
        this.isLoading = false;
        this.cdRef.detectChanges();
      });
  }

  public enable(): void {
    this.analytics.trackActivateTwoFactorAuth('totp');
    this.isLoading = true;
    this.cdRef.detectChanges();
    this.twoFaSmsService.enableAppAuth(this.codeControl.value);
  }

  public onGoBackClick(): void {
    this.goBack.emit();
  }

  public onCancelClick(): void {
    this.cancel.emit();
  }

  public onClearError(): void {
    this.codeError = null;
    this.cdRef.detectChanges();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
